import { Box, Typography, Card, CardContent, CardActions, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const adminMenuItems = [
    {
      title: t("admin.dashboard.checkinSystem"),
      description: t("admin.dashboard.checkinDescription"),
      icon: <QrCodeScannerIcon sx={{ fontSize: 48, color: "#7479AE" }} />,
      path: "/admin/checkin",
      color: "#7479AE",
    },
    {
      title: t("admin.dashboard.reports"),
      description: t("admin.dashboard.reportsDescription"),
      icon: <AssessmentIcon sx={{ fontSize: 48, color: "#e29674" }} />,
      path: "/admin/reports",
      color: "#e29674",
    },
    {
      title: t("admin.dashboard.userManagement"),
      description: t("admin.dashboard.userManagementDescription"),
      icon: <PeopleIcon sx={{ fontSize: 48, color: "#4CAF50" }} />,
      path: "/admin/users",
      color: "#4CAF50",
    },
    {
      title: t("admin.dashboard.settings"),
      description: t("admin.dashboard.settingsDescription"),
      icon: <SettingsIcon sx={{ fontSize: 48, color: "#FF9800" }} />,
      path: "/admin/settings",
      color: "#FF9800",
    },
  ];

  return (
    <Box className="body-content" sx={{ minHeight: "calc(100vh - 72px)", p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h4" sx={{ color: "#7479AE", fontWeight: 700, mb: 1 }}>
            {t("admin.dashboard.title")}
          </Typography>
          <Typography variant="h6" sx={{ color: "#666" }}>
            {t("admin.dashboard.welcome")}, {user.full_name || "Admin"}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
          sx={{ color: "#7479AE", borderColor: "#7479AE" }}
        >
          {t("admin.dashboard.logout")}
        </Button>
      </Box>

      {/* Menu Grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 3, mb: 4 }}>
        {adminMenuItems.map((item, index) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card
              sx={{
                height: "100%",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
              onClick={() => navigate(item.path)}
            >
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <Box sx={{ mb: 2 }}>
                  {item.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{ color: item.color, fontWeight: 600, mb: 1 }}
                >
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  {item.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: "center", pb: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: item.color,
                    color: "white",
                    "&:hover": {
                      bgcolor: item.color,
                      opacity: 0.8,
                    },
                  }}
                >
                  {t("admin.dashboard.accessSystem")}
                </Button>
              </CardActions>
            </Card>
          </motion.div>
        ))}
      </Box>

      {/* Quick Stats */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ color: "#7479AE", fontWeight: 600, mb: 3 }}>
          {t("admin.dashboard.todayStats")}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
          <Card sx={{ bgcolor: "#E8F5E8" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" sx={{ color: "#4CAF50", fontWeight: 700 }}>
                24
              </Typography>
              <Typography variant="body2" sx={{ color: "#2E7D32" }}>
                {t("admin.dashboard.checkinToday")}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ bgcolor: "#FFF3E0" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" sx={{ color: "#FF9800", fontWeight: 700 }}>
                8
              </Typography>
              <Typography variant="body2" sx={{ color: "#E65100" }}>
                {t("admin.dashboard.waiting")}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ bgcolor: "#E3F2FD" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" sx={{ color: "#2196F3", fontWeight: 700 }}>
                16
              </Typography>
              <Typography variant="body2" sx={{ color: "#1565C0" }}>
                {t("admin.dashboard.completed")}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ bgcolor: "#FCE4EC" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" sx={{ color: "#E91E63", fontWeight: 700 }}>
                2
              </Typography>
              <Typography variant="body2" sx={{ color: "#AD1457" }}>
                {t("admin.dashboard.followUp")}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
